(() => {
  if (window.customElements.get('bundle-offer')) {
    return;
  }

  const routesRoot = () => window.Shopify?.routes?.root || '/';

  function formatMoney(cents, format) {
    const value = Math.round(Number(cents)) / 100;
    const group = (amount, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = amount.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0);
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return group(value, 0, ' ');
        default: return group(value, 2);
      }
    });
  }

  class BundleOffer extends HTMLElement {
    connectedCallback() {
      if (this.config) {
        return;
      }
      this.config = JSON.parse(this.querySelector('[data-bo-config]').textContent);
      if (this.config.hideCurrencyCode) {
        // "${{amount}} CAD" -> "${{amount}}"
        this.config.moneyFormat = String(this.config.moneyFormat || '').replace(/\s+[A-Z]{3}\s*$/, '');
      }
      this.variantsById = new Map(this.config.variants.map((variant) => [variant.id, variant]));
      this.optionValues = this.config.options.map((_, position) => [
        ...new Set(this.config.variants.map((variant) => variant.options[position])),
      ]);
      this.tierEls = [...this.querySelectorAll('.bo__tier')];
      this.errorEl = this.querySelector('[data-bo-error]');
      // getAttribute: `form.id` returns the form's <input name="id"> instead of its id.
      this.formId = this.findForm()?.getAttribute('id');
      this.selected = this.config.defaultIndex;
      this.baseVariantId = this.formVariantId() || this.config.selectedVariantId;
      this.items = [];

      // A gift unlocks at the smallest tier (buy + free units) that includes it.
      this.giftMinUnits = new Map();
      for (const tier of this.config.tiers) {
        for (const gift of tier.gifts) {
          const units = tier.buy + tier.free;
          this.giftMinUnits.set(gift.handle, Math.min(units, this.giftMinUnits.get(gift.handle) ?? Infinity));
        }
      }

      this.abortController = new AbortController();
      const {signal} = this.abortController;
      this.addEventListener('change', (event) => this.onChange(event), {signal});
      this.addEventListener('click', (event) => {
        const button = event.target.closest('.bo__color-button');
        if (button) {
          this.openPicker(Number(button.closest('[data-item]').dataset.item), Number(button.dataset.position));
        }
        const sizeLink = event.target.closest('.bo__size-link');
        if (sizeLink) {
          this.sizeGuide()?.open(sizeLink);
        }
        // The gifts box sits outside the tier's label, so tapping it would otherwise do nothing.
        const tier = event.target.closest('.bo__tier');
        if (tier && !tier.classList.contains('is-selected') && !event.target.closest('label, a, button, select, [data-bo-items]')) {
          const input = tier.querySelector('.bo__input');
          input.checked = true;
          input.dispatchEvent(new Event('change', {bubbles: true}));
        }
      }, {signal});
      // The Size guide link needs the size-guide element, whose script may load after this one.
      if (!customElements.get('size-guide') && document.querySelector('size-guide')) {
        customElements.whenDefined('size-guide').then(() => this.isConnected && this.render());
      }
      // Capture phase so the theme's own add-to-cart handler never sees bundle submits.
      document.addEventListener('submit', (event) => this.onSubmit(event), {capture: true, signal});
      document.addEventListener('variant:change', (event) => this.onThemeVariantChange(event), {signal});
      document.addEventListener('change', (event) => {
        if (!this.contains(event.target)) {
          window.setTimeout(() => this.syncBaseVariant(), 0);
        }
      }, {signal});

      this.resetItems();
      this.render();
    }

    disconnectedCallback() {
      this.closePicker(false);
      this.abortController?.abort();
      this.config = null;
    }

    get form() {
      return this.formId ? document.forms[this.formId] : this.findForm();
    }

    get tier() {
      return this.config.tiers[this.selected];
    }

    get units() {
      return this.tier.buy + this.tier.free;
    }

    findForm() {
      const scope = this.closest('.shopify-section') || document;
      return [...scope.querySelectorAll('form[action*="/cart/add"]')].find((form) => form.querySelector('[name="id"]')) || null;
    }

    formVariantId() {
      return Number(this.form?.elements.id?.value) || null;
    }

    onThemeVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant || (event.target !== this.form && event.detail?.formId !== this.formId)) {
        return;
      }
      this.setBaseVariant(variant.id);
    }

    syncBaseVariant() {
      const id = this.formVariantId();
      if (id && id !== this.baseVariantId) {
        this.setBaseVariant(id);
      }
    }

    setBaseVariant(id) {
      if (!this.variantsById.has(id)) {
        return;
      }
      this.baseVariantId = id;
      this.resetItems();
      this.render();
    }

    resetItems() {
      const base = this.variantsById.get(this.baseVariantId);
      this.items = Array.from({length: this.units}, () => this.baseVariantId);
      this.choices = this.items.map(() => [...(base?.options ?? [])]);
      this.animateItems = true;
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('.bo__input')) {
        this.selected = Number(target.value);
        this.resetItems();
        this.render();
        return;
      }
      const row = target.closest('[data-item]');
      if (row) {
        this.setChoice(Number(row.dataset.item), Number(target.dataset.position), target.value);
      }
    }

    setChoice(index, position, value) {
      this.choices[index][position] = value;
      const match = this.config.variants.find((variant) => variant.options.every((v, i) => v === this.choices[index][i]));
      this.items[index] = match ? match.id : null;
      this.render();
      // Rows are rebuilt on every change; keep keyboard focus on the same picker.
      this.querySelector(`.is-selected [data-item="${index}"] [data-position="${position}"]`)?.focus();
    }

    // Whether the item would be in stock with `value` for this option and its other choices kept.
    isAvailable(choices, position, value) {
      const options = choices.map((v, i) => (i === position ? value : v));
      return Boolean(this.config.variants.find((v) => v.options.every((o, i) => o === options[i]))?.available);
    }

    priceFor(variantIds, tier) {
      const prices = variantIds.map((id) => this.variantsById.get(id)?.price ?? 0).sort((a, b) => a - b);
      const total = prices.reduce((sum, price) => sum + price, 0);
      const free = prices.slice(0, tier.free).reduce((sum, price) => sum + price, 0);
      let compare = total;
      if (this.config.compareBasis === 'compare_at_price') {
        compare = variantIds.reduce((sum, id) => {
          const variant = this.variantsById.get(id);
          return sum + Math.max(variant?.compareAtPrice ?? 0, variant?.price ?? 0);
        }, 0);
      }
      let pay = total - free;
      // Display-only: mirrors the store's automatic quantity discount for this tier.
      // Shopify rounds a percentage discount down to the cent per unit.
      if (tier.discountPercent > 0) {
        pay = prices.slice(tier.free).reduce((sum, price) => sum + price - Math.floor((price * tier.discountPercent) / 100), 0);
      }
      // `half` of the paid units (the cheapest after the free ones) are 50% off, as the discount function does.
      if (tier.half > 0) {
        pay -= prices.slice(tier.free, tier.free + tier.half).reduce((sum, price) => sum + Math.floor(price / 2), 0);
      }
      return {pay, compare};
    }

    // "For you", "For them"… from the tier, else "Pair 1", "Pair 2"…
    itemLabel(index) {
      return this.tier?.itemLabels?.[index] || `${this.config.itemLabel}${index + 1}`;
    }

    render() {
      const money = (cents) => formatMoney(cents, this.config.moneyFormat);

      this.tierEls.forEach((el, index) => {
        const tier = this.config.tiers[index];
        const units = tier.buy + tier.free;
        const selected = index === this.selected;
        const ids = selected
          ? this.items.map((id) => id ?? this.baseVariantId)
          : Array.from({length: units}, () => this.baseVariantId);
        const {pay, compare} = this.priceFor(ids, tier);
        const saved = Math.max(compare - pay, 0);
        const percent = compare > 0 ? Math.round((saved / compare) * 100) : 0;
        const fill = (template) => template
          .replaceAll('{per_unit}', money(Math.round(pay / units)))
          .replaceAll('{saved}', money(saved))
          .replaceAll('{percent}', String(percent));

        el.classList.toggle('is-selected', selected);
        el.querySelector('.bo__input').checked = selected;
        el.querySelector('[data-bo-price]').textContent = money(pay);
        const compareEl = el.querySelector('[data-bo-compare]');
        compareEl.textContent = money(compare);
        compareEl.hidden = compare <= pay;

        const saveEl = el.querySelector('[data-bo-save]');
        const saveTemplate = tier.saveLabel || this.config.saveTemplate || '';
        saveEl.textContent = fill(saveTemplate);
        saveEl.hidden = percent <= 0 || !saveTemplate;

        el.querySelector('[data-bo-subtitle]').textContent = fill(tier.subtitle || this.config.subtitleTemplate || '');

        this.renderStack(el.querySelector('[data-bo-stack]'), ids);

        const itemsEl = el.querySelector('[data-bo-items]');
        const showItems = selected && this.config.showSelectors && units > 1 && this.config.options.length > 0;
        itemsEl.classList.toggle('is-open', showItems);
        if (showItems) {
          this.renderItems(itemsEl.firstElementChild);
        } else {
          itemsEl.firstElementChild.replaceChildren();
        }
      });

      this.animateItems = false;
      this.showError(this.hasUnavailableItem() ? this.config.unavailableMessage : '');
    }

    // Fanned photos for a tier, up to three, leading with each distinct variant image.
    renderStack(stack, ids) {
      if (!stack) {
        return;
      }
      const images = ids.map((id) => this.variantsById.get(id)?.image || this.config.fallbackImage).filter(Boolean);
      const unique = [...new Set(images)];
      if (!unique.length) {
        return;
      }
      const slots = Math.min(ids.length, 3);
      const chosen = Array.from({length: slots}, (_, i) => unique[i % unique.length]);
      // The last image sits on top, so the first choice goes last.
      const ordered = slots > 1 ? [...chosen.slice(1), chosen[0]] : chosen;
      stack.querySelectorAll('.bo__stack-img').forEach((img, i) => {
        if (ordered[i] && img.getAttribute('src') !== ordered[i]) {
          img.removeAttribute('srcset');
          img.src = ordered[i];
        }
      });
    }

    renderItems(container) {
      const colorPosition = this.config.options.findIndex((name) => /colou?r|couleur|farbe|colore/i.test(name));

      const rows = this.items.map((id, index) => {
        const variant = this.variantsById.get(id);
        const choices = this.choices[index];
        const row = document.createElement('div');
        row.className = 'bo__item';
        row.dataset.item = String(index);
        if (this.animateItems) {
          row.style.setProperty('--bo-row', String(index));
        } else {
          row.style.animation = 'none';
        }

        const label = document.createElement('span');
        label.className = 'bo__item-label';
        label.textContent = this.itemLabel(index);
        row.append(label);

        this.config.options.forEach((name, position) => {
          const field = document.createElement('span');
          field.className = 'bo__field';

          // Colours with photos get a photo picker instead of a dropdown.
          if (position === colorPosition && this.hasOptionImages(position)) {
            field.classList.add('bo__field--color');
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'bo__select bo__color-button';
            button.dataset.position = String(position);
            button.setAttribute('aria-haspopup', 'dialog');
            button.setAttribute('aria-label', `${name} ${index + 1}: ${choices[position] ?? ''}`);
            const image = this.imageForOption(position, choices[position]);
            if (image) {
              const swatch = document.createElement('img');
              swatch.className = 'bo__swatch bo__swatch--image';
              swatch.src = image;
              swatch.alt = '';
              swatch.width = 30;
              swatch.height = 30;
              button.append(swatch);
              button.classList.add('has-swatch');
            }
            const text = document.createElement('span');
            text.className = 'bo__color-name';
            text.textContent = choices[position] ?? '';
            button.append(text);
            field.append(button);
            row.append(field);
            return;
          }

          // Otherwise a dot when the value is a CSS color name ("Dark Red" -> darkred).
          if (position === colorPosition) {
            field.classList.add('bo__field--color');
            const image = this.imageForOption(position, choices[position]);
            const color = String(choices[position] ?? '').toLowerCase().replace(/\s+/g, '');
            if (image) {
              const swatch = document.createElement('img');
              swatch.className = 'bo__swatch bo__swatch--image';
              swatch.src = image;
              swatch.alt = '';
              swatch.width = 30;
              swatch.height = 30;
              field.append(swatch);
            } else if (color && window.CSS?.supports?.('color', color)) {
              const swatch = document.createElement('span');
              swatch.className = 'bo__swatch';
              swatch.style.background = color;
              field.append(swatch);
            }
          }

          const select = document.createElement('select');
          select.className = 'bo__select';
          select.dataset.position = String(position);
          select.setAttribute('aria-label', `${name} ${index + 1}`);
          for (const value of this.optionValues[position]) {
            const option = new Option(value, value);
            // Mark the value sold out when no available variant matches it with the row's other choices.
            if (!this.isAvailable(choices, position, value)) {
              option.textContent = `${value} - ${this.config.soldOutLabel}`;
            }
            option.selected = choices[position] === value;
            select.append(option);
          }
          field.append(select);
          row.append(field);
        });

        if (!variant || !variant.available) {
          row.classList.add('is-unavailable');
        }
        return row;
      });

      // The product's Size guide drawer (Size guide block), reachable from the pair pickers too.
      if (this.config.sizeGuideLabel && this.sizeGuide()) {
        const link = document.createElement('button');
        link.type = 'button';
        link.className = 'bo__size-link';
        link.setAttribute('aria-haspopup', 'dialog');
        link.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="1.5" y="7" width="21" height="10" rx="1.5" stroke="currentColor" stroke-width="1.5"/><path d="M5.5 7v3.5M9 7v2.2M12.5 7v3.5M16 7v2.2M19.5 7v3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        const text = document.createElement('span');
        text.textContent = this.config.sizeGuideLabel;
        link.append(text);
        rows.push(link);
      }
      container.classList.toggle('has-named-labels', Array.isArray(this.tier?.itemLabels) && this.tier.itemLabels.length > 0);
      container.replaceChildren(...rows);
    }

    sizeGuide() {
      const guide = this.closest('.shopify-section')?.querySelector('size-guide') || document.querySelector('size-guide');
      return guide && typeof guide.open === 'function' ? guide : null;
    }

    hasOptionImages(position) {
      return this.optionValues[position].some((value) => this.imageForOption(position, value));
    }

    // Bottom sheet (centered dialog on desktop) with a photo for every value of an option.
    openPicker(index, position) {
      this.closePicker(false);
      const name = this.config.options[position];
      const choices = this.choices[index];
      const titleId = `bo-sheet-title-${Date.now()}`;
      const el = (tag, className, text) => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text != null) node.textContent = text;
        return node;
      };

      const sheet = el('div', 'bo-sheet');
      // The sheet is attached to <body> to escape the theme's stacking contexts, so carry the accent over.
      sheet.style.setProperty('--bo-accent', getComputedStyle(this).getPropertyValue('--bo-accent').trim() || '#1a1a1a');
      const backdrop = el('div', 'bo-sheet__backdrop');
      const panel = el('div', 'bo-sheet__panel');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-labelledby', titleId);

      const header = el('div', 'bo-sheet__header');
      const title = el('p', 'bo-sheet__title', `${this.itemLabel(index)} · ${name}`);
      title.id = titleId;
      const close = el('button', 'bo-sheet__close');
      close.type = 'button';
      close.setAttribute('aria-label', 'Close');
      close.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
      header.append(title, close);

      const grid = el('div', 'bo-sheet__grid');
      grid.setAttribute('role', 'listbox');
      grid.setAttribute('aria-label', name);
      for (const value of this.optionValues[position]) {
        const selected = choices[position] === value;
        const available = this.isAvailable(choices, position, value);
        const option = el('button', 'bo-sheet__option');
        option.type = 'button';
        option.dataset.value = value;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(selected));
        option.classList.toggle('is-selected', selected);
        option.classList.toggle('is-soldout', !available);
        const image = this.imageForOption(position, value);
        if (image) {
          const img = el('img');
          img.src = image.replace(/([?&]width=)\d+/, (_, prefix) => `${prefix}300`);
          img.alt = '';
          img.loading = 'lazy';
          option.append(img);
        }
        option.append(el('span', 'bo-sheet__name', value));
        if (!available) {
          option.append(el('span', 'bo-sheet__note', this.config.soldOutLabel));
        }
        grid.append(option);
      }

      panel.append(el('span', 'bo-sheet__handle'), header, grid);
      sheet.append(backdrop, panel);
      document.body.append(sheet);
      document.documentElement.classList.add('bo-sheet-lock');

      const options = [...grid.querySelectorAll('.bo-sheet__option')];
      const columns = () => Math.max(1, Math.round(grid.clientWidth / (options[0]?.offsetWidth || grid.clientWidth)));
      backdrop.addEventListener('click', () => this.closePicker(true));
      close.addEventListener('click', () => this.closePicker(true));
      grid.addEventListener('click', (event) => {
        const option = event.target.closest('.bo-sheet__option');
        if (!option) return;
        this.closePicker(false);
        this.setChoice(index, position, option.dataset.value);
      });
      sheet.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closePicker(true);
          return;
        }
        const current = options.indexOf(document.activeElement);
        const moves = {ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns(), ArrowUp: -columns()};
        if (current >= 0 && event.key in moves) {
          event.preventDefault();
          options[Math.min(options.length - 1, Math.max(0, current + moves[event.key]))].focus();
        } else if (event.key === 'Tab') {
          // Keep focus inside the dialog.
          const focusable = [close, ...options];
          const at = focusable.indexOf(document.activeElement);
          const next = event.shiftKey ? (at <= 0 ? focusable.length - 1 : at - 1) : (at + 1) % focusable.length;
          event.preventDefault();
          focusable[next].focus();
        }
      });

      this.picker = {sheet, index, position};
      requestAnimationFrame(() => sheet.classList.add('is-open'));
      (options.find((option) => option.classList.contains('is-selected')) || options[0] || close).focus({preventScroll: true});
    }

    closePicker(returnFocus) {
      const picker = this.picker;
      if (!picker) return;
      this.picker = null;
      picker.sheet.classList.remove('is-open');
      document.documentElement.classList.remove('bo-sheet-lock');
      window.setTimeout(() => picker.sheet.remove(), 320);
      if (returnFocus) {
        this.querySelector(`.is-selected [data-item="${picker.index}"] [data-position="${picker.position}"]`)?.focus();
      }
    }

    // First variant image for an option value, e.g. the photo of the "Dark Red" boots.
    imageForOption(position, value) {
      return this.config.variants.find((variant) => variant.options[position] === value && variant.image)?.image ?? null;
    }

    hasUnavailableItem() {
      return this.items.some((id) => !this.variantsById.get(id)?.available);
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    async onSubmit(event) {
      const form = this.form;
      if (!form || event.target !== form) {
        return;
      }
      const tier = this.tier;
      if (this.units === 1 && tier.gifts.length === 0) {
        return; // A single item without gifts is a normal add to cart.
      }
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.submitting) {
        return;
      }
      if (this.hasUnavailableItem()) {
        this.showError(this.config.unavailableMessage);
        return;
      }

      const quantities = new Map();
      for (const id of this.items) {
        quantities.set(id, (quantities.get(id) ?? 0) + 1);
      }
      const items = [...quantities].map(([id, quantity]) => ({id, quantity}));
      for (const gift of tier.gifts) {
        items.push({
          id: gift.variantId,
          quantity: 1,
          properties: {_bundle_gift: `${this.config.productId}:${this.giftMinUnits.get(gift.handle)}`},
        });
      }

      const sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: {sections},
      }));

      const buttons = [...form.elements].filter((element) => element.type === 'submit');
      buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
      this.submitting = true;
      this.showError('');

      try {
        const response = await window.fetch(`${routesRoot()}cart/add.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({
            items,
            sections: sections.join(','),
            sections_url: `${routesRoot()}variants/${this.items[0]}`,
          }),
        });
        const result = await response.json();

        if (!response.ok) {
          const message = result.description || result.message || 'Unable to add this bundle to your cart.';
          this.showError(message);
          form.dispatchEvent(new CustomEvent('cart:error', {bubbles: true, detail: {error: message}}));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${routesRoot()}cart`;
          return;
        }

        const cart = await (await window.fetch(`${routesRoot()}cart.js`)).json();
        cart.sections = result.sections;
        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {items: result.items ?? [result], cart},
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {baseEvent: 'variant:add', cart},
        }));
      } catch (error) {
        console.error('[bundle-offer]', error);
        this.showError('Unable to add this bundle to your cart. Please try again.');
      } finally {
        this.submitting = false;
        buttons.forEach((button) => button.removeAttribute('aria-busy'));
      }
    }
  }

  window.customElements.define('bundle-offer', BundleOffer);
})();
